import { Injectable } from '@angular/core';

/**
 * The open anchored popups, in the order they opened. A popup opened from inside another
 * one — the entity popover a finding card's reference row opens — renders in its own
 * outlet at the page root, so it is not a DOM descendant of the popup that opened it.
 * Without this order the lower popup reads a click in the upper one as an outside click
 * and dismisses itself.
 */
@Injectable({ providedIn: 'root' })
export class AnchoredPopupStackService {
  /** Matches the popup's `z-100` utility class; later popups only need to clear earlier ones. */
  private static readonly BASE_Z_INDEX = 100;

  private readonly stack: HTMLElement[] = [];
  private nextZIndex = AnchoredPopupStackService.BASE_Z_INDEX;
  private opening: unknown = null;

  /**
   * A document click listener runs synchronously within the same dispatch as the click
   * that triggered `open()`, before change detection has pushed the popup's new anchor to
   * `AnchoredPopupComponent`. Without this flag that listener still compares the click
   * against the previous anchor, reads it as an outside click, and dismisses the popup that
   * the same click just asked to move. The flag only needs to survive that one dispatch, so
   * a microtask — which always runs before the next task, but after the current one finishes
   * — is exactly the lifetime it needs.
   *
   * `owner` is the popup state instance whose `open()` call this is — a card opening a new
   * occurrence must not also suppress an unrelated entity popover's own dismissal, so the
   * flag records *which* popup is mid-open rather than just that one is.
   */
  markOpening(owner: unknown): void {
    this.opening = owner;
    queueMicrotask(() => {
      if (this.opening === owner) this.opening = null;
    });
  }

  /**
   * `owner` is whatever the calling `AnchoredPopupComponent` was told to watch via its
   * `owner` input. A component with no owner configured has no way to tell "its own"
   * reopen from anyone else's, so it falls back to suppressing on any in-progress open —
   * the coarser behaviour every anchored popup had before per-owner tracking existed.
   */
  isOpening(owner?: unknown): boolean {
    if (this.opening === null) return false;
    return owner === undefined || this.opening === owner;
  }

  push(el: HTMLElement): void {
    this.stack.push(el);
    // Tied CSS z-index falls back to DOM source order for paint and hit-testing, which can
    // put a logically topmost popup underneath one mounted earlier in the template (e.g. an
    // entity popover opened from a finding card that renders in an outlet mounted before the
    // card's). An inline z-index keyed to push order — not array position, which would repeat
    // once an earlier popup is removed — keeps paint order matching stack order.
    el.style.zIndex = String(this.nextZIndex++);
  }

  remove(el: HTMLElement): void {
    const index = this.stack.indexOf(el);
    if (index !== -1) this.stack.splice(index, 1);
    // Otherwise the counter only grows across a session and eventually crosses a real
    // threshold: the tooltip singleton's z-200, then the app's context menus at z-1000.
    if (this.stack.length === 0) this.nextZIndex = AnchoredPopupStackService.BASE_Z_INDEX;
  }

  hasOpen(): boolean {
    return this.stack.length > 0;
  }

  isTopmost(el: HTMLElement): boolean {
    return this.stack.at(-1) === el;
  }

  /** The popups opened after `el`; a click inside one of them belongs to `el`'s chain. */
  above(el: HTMLElement): HTMLElement[] {
    const index = this.stack.indexOf(el);
    return index === -1 ? [] : this.stack.slice(index + 1);
  }
}
